import React from 'react'

import { useT } from '../../i18n'
import { groupCrewLocationOptions, isLotStream, type CrewLocationOption, type CrewLocationStream } from './crewLocation'

// Crew Mode Field Bundle v0 §3 items 1, 3, 4 and §6–§7: large targets
// (≥ 56 px), dark text on light solid backgrounds (WCAG 2.2 SC 1.4.3 / 1.4.11).

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '10px 14px',
  borderRadius: 12,
  backgroundColor: '#eff6ff',
  border: '2px solid #1d4ed8',
  color: '#0f172a'
}

const bigButtonStyle: React.CSSProperties = {
  minHeight: 56,
  borderRadius: 12,
  border: '1px solid #cbd5e1',
  backgroundColor: 'white',
  color: '#0f172a',
  fontWeight: 800,
  fontSize: 18,
  cursor: 'pointer',
  padding: '10px 14px',
  width: '100%',
  textAlign: 'left'
}

const changeButtonStyle: React.CSSProperties = {
  minHeight: 48,
  minWidth: 96,
  borderRadius: 10,
  border: '1px solid #1d4ed8',
  backgroundColor: 'white',
  color: '#1d4ed8',
  fontWeight: 800,
  fontSize: 15,
  cursor: 'pointer',
  padding: '6px 12px'
}

export function CrewLocationHeader({
  stream,
  location,
  onChange
}: Readonly<{ stream: CrewLocationStream; location: CrewLocationOption | null; onChange: () => void }>): React.ReactElement {
  const t = useT()
  return (
    <div data-testid="crew-location-header" style={headerStyle}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>{t('crewMode.location.youAreIn')}</div>
        <div data-testid="crew-location-current" style={{ fontSize: 22, fontWeight: 900, overflowWrap: 'anywhere' }}>
          {location ? location.label : t(isLotStream(stream) ? 'crewMode.location.noneLot' : 'crewMode.location.noneUnit')}
        </div>
      </div>
      <button type="button" data-testid="crew-location-change" onClick={onChange} style={changeButtonStyle}>
        {t('crewMode.location.change')}
      </button>
    </div>
  )
}

export function CrewLocationPicker({
  stream,
  open,
  options,
  currentKey,
  onChoose,
  onClose
}: Readonly<{
  stream: CrewLocationStream
  open: boolean
  options: CrewLocationOption[]
  currentKey?: string
  onChoose: (option: CrewLocationOption) => void
  onClose: () => void
}>): React.ReactElement | null {
  const t = useT()
  if (!open) return null
  const groups = groupCrewLocationOptions(stream, options)
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('crewMode.location.pickerTitle')}
      data-testid="crew-location-picker"
      style={{ position: 'fixed', inset: 0, zIndex: 1000, backgroundColor: '#f8fafc', overflowY: 'auto', padding: 16 }}
    >
      <div style={{ maxWidth: 420, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 22, color: '#0f172a' }}>{t('crewMode.location.pickerTitle')}</h2>
          {currentKey ? (
            <button type="button" data-testid="crew-location-picker-close" onClick={onClose} style={changeButtonStyle}>
              {t('crewMode.location.close')}
            </button>
          ) : null}
        </div>
        {options.length === 0 ? (
          <div data-testid="crew-location-picker-empty" style={{ padding: 14, borderRadius: 10, backgroundColor: '#fef2f2', color: '#991b1b', fontWeight: 700 }}>
            {t(isLotStream(stream) ? 'crewMode.location.emptyLot' : 'crewMode.location.emptyUnit')}
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.heading ?? 'units'} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {group.heading ? (
                <div style={{ fontSize: 13, fontWeight: 800, color: '#334155', marginTop: 4 }}>{group.heading}</div>
              ) : null}
              {group.options.map((option) => {
                const selected = option.key === currentKey
                return (
                  <button
                    key={option.key}
                    type="button"
                    data-testid={`crew-location-option-${option.key}`}
                    onClick={() => onChoose(option)}
                    aria-pressed={selected}
                    style={{
                      ...bigButtonStyle,
                      borderColor: selected ? '#1d4ed8' : '#cbd5e1',
                      borderWidth: selected ? 2 : 1,
                      backgroundColor: selected ? '#eff6ff' : 'white'
                    }}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export function CrewNextLocationButton({
  next,
  onGo
}: Readonly<{ next: CrewLocationOption | null; onGo: (option: CrewLocationOption) => void }>): React.ReactElement | null {
  const t = useT()
  if (!next) return null
  const label = next.lotKey ? `${next.unitName} · ${next.lotKey}` : next.unitName
  return (
    <button
      type="button"
      data-testid="crew-location-next"
      onClick={() => onGo(next)}
      style={{ ...bigButtonStyle, textAlign: 'center', backgroundColor: '#f1f5f9', fontSize: 16 }}
    >
      {t('crewMode.location.next', { label })}
    </button>
  )
}
